import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { styled } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
  Legend
} from 'recharts';
import { Users, PhoneCall, Activity, TrendingUp, BarChart3 as HrKpiLogoIcon } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-toastify';

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
  height: '100%'
}));

const ChartWrapper = styled(Box)(({ theme }) => ({
  height: 320,
  width: '100%',
  marginTop: theme.spacing(2)
}));

export default function HrKpi() {
  const [loading, setLoading] = useState(true);
  const [totals, setTotals] = useState({ candidates: 0, interviews: 0 });
  const [byFonte, setByFonte] = useState([]);
  const [byPosizione, setByPosizione] = useState([]);
  const [byStato, setByStato] = useState([]);
  const [timeSeriesByDay, setTimeSeriesByDay] = useState([]);
  const [monthlyComparison, setMonthlyComparison] = useState(null);
  const [conversion, setConversion] = useState({
    programmato: 0,
    assunto: 0,
    ratioAssuntoSuProgrammato: 0
  });
  const [fromDate, setFromDate] = useState('');
  const [untilDate, setUntilDate] = useState('');
  const [rangePreset, setRangePreset] = useState('all');

  const getRangeFromPreset = (preset) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (preset === 'all') {
      return { from: '', until: '' };
    }

    const to = new Date(today);
    const from = new Date(today);

    if (preset === '7d') {
      from.setDate(from.getDate() - 6);
    } else if (preset === '30d') {
      from.setDate(from.getDate() - 29);
    } else if (preset === '90d') {
      from.setDate(from.getDate() - 89);
    } else {
      return { from: '', until: '' };
    }

    const format = (d) => d.toISOString().slice(0, 10);
    return { from: format(from), until: format(to) };
  };

  const fetchKpi = async (overrideRange) => {
    try {
      setLoading(true);
      const params = {};
      const range = overrideRange || { from: fromDate, until: untilDate };
      if (range.from) params.from = range.from;
      if (range.until) params.until = range.until;

      const res = await api.get('/hr/kpi', { params });
      const data = res.data || {};
      setTotals(data.totals || { candidates: 0, interviews: 0 });
      setByFonte(data.byFonte || []);
      setByPosizione(data.byPosizione || []);
      setByStato(data.byStato || []);
      setTimeSeriesByDay(data.timeSeriesByDay || []);
      setMonthlyComparison(data.monthlyComparison || null);
      setConversion(
        data.conversion || {
          programmato: 0,
          assunto: 0,
          ratioAssuntoSuProgrammato: 0
        }
      );
    } catch (error) {
      console.error('Error fetching HR KPI:', error);
      toast.error('Errore nel caricamento dei KPI HR');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKpi();
  }, []);

  const totalFonte = byFonte.reduce((sum, f) => sum + (f.value || 0), 0);
  const totalPosizione = byPosizione.reduce((sum, p) => sum + (p.value || 0), 0);
  const totalStato = byStato.reduce((sum, s) => sum + (s.value || 0), 0);

  const statoColorMap = {
    programmato: '#f59e0b',
    sostenuto: '#3b82f6',
    chiuso: '#64748b',
    rifiutato: '#ef4444',
    assunto: '#22c55e',
    non_idoneo: '#9ca3af'
  };

  const formatDateIt = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) {
      const iso = String(value).slice(0, 10);
      const parts = iso.split('-');
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
      }
      return String(value);
    }
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const monthlyBars = (() => {
    if (!monthlyComparison || !Array.isArray(monthlyComparison.rows)) {
      return [];
    }
    const months = [
      'gen',
      'feb',
      'mar',
      'apr',
      'mag',
      'giu',
      'lug',
      'ago',
      'set',
      'ott',
      'nov',
      'dic'
    ];
    const base = months.map((label, index) => ({
      month: index + 1,
      label,
      current: 0,
      previous: 0
    }));
    monthlyComparison.rows.forEach((row) => {
      const monthIndex = row.month - 1;
      if (monthIndex < 0 || monthIndex >= base.length) return;
      if (row.year === monthlyComparison.currentYear) {
        base[monthIndex].current = row.total || 0;
      } else if (row.year === monthlyComparison.previousYear) {
        base[monthIndex].previous = row.total || 0;
      }
    });
    return base;
  })();

  const handleRangePresetChange = (event) => {
    const value = event.target.value;
    setRangePreset(value);

    if (value === 'custom') {
      return;
    }

    const range = getRangeFromPreset(value);
    setFromDate(range.from);
    setUntilDate(range.until);
    fetchKpi(range);
  };

  const handleFromDateChange = (event) => {
    const value = event.target.value;
    setFromDate(value);

    if (rangePreset !== 'custom') {
      return;
    }

    const range = { from: value, until: untilDate };
    if (range.from && range.until) {
      fetchKpi(range);
    }
  };

  const handleUntilDateChange = (event) => {
    const value = event.target.value;
    setUntilDate(value);

    if (rangePreset !== 'custom') {
      return;
    }

    const range = { from: fromDate, until: value };
    if (range.from && range.until) {
      fetchKpi(range);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          gap: 2,
          flexWrap: 'wrap'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <HrKpiLogoIcon size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
              KPI HR Candidati
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Indicatori di recruiting, colloqui e assunzioni
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="kpi-range-label">Intervallo</InputLabel>
            <Select
              labelId="kpi-range-label"
              label="Intervallo"
              value={rangePreset}
              onChange={handleRangePresetChange}
            >
              <MenuItem value="all">Tutto lo storico</MenuItem>
              <MenuItem value="7d">Ultimi 7 giorni</MenuItem>
              <MenuItem value="30d">Ultimi 30 giorni</MenuItem>
              <MenuItem value="90d">Ultimi 90 giorni</MenuItem>
              <MenuItem value="custom">Personalizzato</MenuItem>
            </Select>
          </FormControl>
          <TextField
            type="date"
            size="small"
            label="Dal"
            value={fromDate}
            onChange={handleFromDateChange}
            InputLabelProps={{ shrink: true }}
            disabled={rangePreset !== 'custom'}
          />
          <TextField
            type="date"
            size="small"
            label="Al"
            value={untilDate}
            onChange={handleUntilDateChange}
            InputLabelProps={{ shrink: true }}
            disabled={rangePreset !== 'custom'}
          />
        </Box>
      </Box>

      {loading ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 320
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <StyledCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      bgcolor: 'rgba(37, 99, 235, 0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#2563eb'
                    }}
                  >
                    <Users size={18} />
                  </Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Totale candidati
                  </Typography>
                </Box>
                <Typography variant="h3" sx={{ mt: 1.5, fontWeight: 'bold' }}>
                  {totals.candidates}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Profili unici presenti in anagrafica HR.
                </Typography>
              </CardContent>
            </StyledCard>
          </Grid>
          <Grid item xs={12} md={3}>
            <StyledCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      bgcolor: 'rgba(16, 185, 129, 0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#059669'
                    }}
                  >
                    <PhoneCall size={18} />
                  </Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Totale colloqui
                  </Typography>
                </Box>
                <Typography variant="h3" sx={{ mt: 1.5, fontWeight: 'bold' }}>
                  {totals.interviews}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Numero complessivo di colloqui registrati nel periodo.
                </Typography>
              </CardContent>
            </StyledCard>
          </Grid>
          <Grid item xs={12} md={3}>
            <StyledCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      bgcolor: 'rgba(79, 70, 229, 0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#4f46e5'
                    }}
                  >
                    <Activity size={18} />
                  </Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Media colloqui per candidato
                  </Typography>
                </Box>
                <Typography variant="h3" sx={{ mt: 1.5, fontWeight: 'bold' }}>
                  {totals.candidates > 0
                    ? (totals.interviews / totals.candidates).toFixed(2)
                    : '0.00'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Indicatore medio di attività su ciascun profilo.
                </Typography>
              </CardContent>
            </StyledCard>
          </Grid>
          <Grid item xs={12} md={3}>
            <StyledCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      bgcolor: 'rgba(249, 115, 22, 0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#f97316'
                    }}
                  >
                    <TrendingUp size={18} />
                  </Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Conversione programmato → assunto
                  </Typography>
                </Box>
                <Typography variant="h3" sx={{ mt: 1.5, fontWeight: 'bold' }}>
                  {conversion.ratioAssuntoSuProgrammato
                    ? (conversion.ratioAssuntoSuProgrammato * 100).toFixed(1) + '%'
                    : '0.0%'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Programmato: {conversion.programmato || 0} • Assunto:{' '}
                  {conversion.assunto || 0}
                </Typography>
              </CardContent>
            </StyledCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <StyledCard>
              <CardContent>
                <Typography variant="h6">Candidati per fonte</Typography>
                <Typography variant="body2" color="text.secondary">
                  Distribuzione dei candidati rispetto al canale di acquisizione.
                </Typography>
                <ChartWrapper>
                  {byFonte.length === 0 ? (
                    <Box
                      sx={{
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Nessun dato disponibile
                      </Typography>
                    </Box>
                  ) : (
                    <ResponsiveContainer>
                      <PieChart>
                        <Tooltip
                          formatter={(value, name, props) => {
                            const v = Number(value) || 0;
                            const pct =
                              totalFonte > 0 ? ((v / totalFonte) * 100).toFixed(1) + '%' : '0.0%';
                            return [`${v} (${pct})`, 'Candidati'];
                          }}
                        />
                        <Legend />
                        <Pie
                          data={byFonte}
                          dataKey="value"
                          nameKey="label"
                          cx="50%"
                          cy="50%"
                          innerRadius={70}
                          outerRadius={110}
                          paddingAngle={2}
                          labelLine={false}
                          label={(props) => {
                            const v = Number(props.value) || 0;
                            if (!totalFonte || !v) return null;
                            const pct = ((v / totalFonte) * 100).toFixed(1);
                            return `${pct}%`;
                          }}
                        >
                          {byFonte.map((entry, index) => (
                            <Cell
                              key={entry.label}
                              fill={['#2563eb', '#f59e0b', '#22c55e', '#ef4444', '#6b7280'][
                                index % 5
                              ]}
                            />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </ChartWrapper>
              </CardContent>
            </StyledCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <StyledCard>
              <CardContent>
                <Typography variant="h6">
                  Confronto colloqui per mese (anno corrente vs precedente)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Confronto del volume di colloqui per mese tra anno corrente e precedente.
                </Typography>
                <ChartWrapper>
                  {!monthlyBars.length ? (
                    <Box
                      sx={{
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Nessun dato disponibile
                      </Typography>
                    </Box>
                  ) : (
                    <ResponsiveContainer>
                      <BarChart
                        data={monthlyBars}
                        margin={{ top: 16, right: 32, left: 16, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="label" />
                        <YAxis allowDecimals={false} />
                        <Tooltip />
                        <Legend
                          formatter={(value) =>
                            value === 'current'
                              ? `Anno ${monthlyComparison.currentYear}`
                              : `Anno ${monthlyComparison.previousYear}`
                          }
                        />
                        <Bar dataKey="current" fill="#2563eb" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="previous" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </ChartWrapper>
              </CardContent>
            </StyledCard>
          </Grid>

          <Grid item xs={12}>
            <StyledCard>
              <CardContent>
                <Typography variant="h6">Stato dei colloqui</Typography>
                <Typography variant="body2" color="text.secondary">
                  Ripartizione dei colloqui per stato di avanzamento del processo.
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={7}>
                    <ChartWrapper>
                      {byStato.length === 0 ? (
                        <Box
                          sx={{
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            Nessun dato disponibile
                          </Typography>
                        </Box>
                      ) : (
                        <ResponsiveContainer>
                          <BarChart
                            data={byStato}
                            layout="vertical"
                            margin={{ top: 16, right: 32, left: 80, bottom: 0 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis type="number" allowDecimals={false} />
                            <YAxis type="category" dataKey="label" />
                            <Tooltip
                              formatter={(value) => {
                                const v = Number(value) || 0;
                                const pct =
                                  totalStato > 0
                                    ? ((v / totalStato) * 100).toFixed(1) + '%'
                                    : '0.0%';
                                return [`${v} (${pct})`, 'Colloqui'];
                              }}
                            />
                            <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                              {byStato.map((entry) => (
                                <Cell
                                  key={entry.label}
                                  fill={statoColorMap[entry.label] || '#f97316'}
                                />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </ChartWrapper>
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <ChartWrapper>
                      {byStato.length === 0 ? (
                        <Box
                          sx={{
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            Nessun dato disponibile
                          </Typography>
                        </Box>
                      ) : (
                        <ResponsiveContainer>
                          <PieChart>
                            <Tooltip
                              formatter={(value) => {
                                const v = Number(value) || 0;
                                const pct =
                                  totalStato > 0
                                    ? ((v / totalStato) * 100).toFixed(1) + '%'
                                    : '0.0%';
                                return [`${v} (${pct})`, 'Colloqui'];
                              }}
                            />
                            <Legend />
                            <Pie
                              data={byStato}
                              dataKey="value"
                              nameKey="label"
                              cx="50%"
                              cy="50%"
                              innerRadius={60}
                              outerRadius={100}
                              paddingAngle={2}
                              labelLine={false}
                              label={(props) => {
                                const v = Number(props.value) || 0;
                                if (!totalStato || !v) return null;
                                const pct = ((v / totalStato) * 100).toFixed(1);
                                return `${pct}%`;
                              }}
                            >
                              {byStato.map((entry) => (
                                <Cell
                                  key={entry.label}
                                  fill={statoColorMap[entry.label] || '#9ca3af'}
                                />
                              ))}
                            </Pie>
                          </PieChart>
                        </ResponsiveContainer>
                      )}
                    </ChartWrapper>
                  </Grid>
                </Grid>
              </CardContent>
            </StyledCard>
          </Grid>
          <Grid item xs={12}>
            <StyledCard>
              <CardContent>
                <Typography variant="h6">Colloqui nel tempo</Typography>
                <Typography variant="body2" color="text.secondary">
                  Andamento giornaliero dei colloqui per stato nel periodo selezionato.
                </Typography>
                <ChartWrapper>
                  {timeSeriesByDay.length === 0 ? (
                    <Box
                      sx={{
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Nessun dato disponibile
                      </Typography>
                    </Box>
                  ) : (
                    <ResponsiveContainer>
                      <BarChart
                        data={timeSeriesByDay}
                        margin={{ top: 16, right: 32, left: 16, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="day" tickFormatter={formatDateIt} />
                        <YAxis allowDecimals={false} />
                        <Tooltip labelFormatter={(label) => formatDateIt(label)} />
                        <Legend
                          formatter={(value) => {
                            if (value === 'programmato') return 'Programmato';
                            if (value === 'sostenuto') return 'Sostenuto';
                            if (value === 'rifiutato') return 'Proposta rifiutata';
                            if (value === 'assunto') return 'Assunto';
                            if (value === 'non_idoneo') return 'Non idoneo';
                            return value;
                          }}
                        />
                        <Bar
                          dataKey="programmato"
                          name="Programmato"
                          fill={statoColorMap.programmato}
                        />
                        <Bar
                          dataKey="sostenuto"
                          name="Sostenuto"
                          fill={statoColorMap.sostenuto}
                        />
                        <Bar
                          dataKey="rifiutato"
                          name="Proposta rifiutata"
                          fill={statoColorMap.rifiutato}
                        />
                        <Bar
                          dataKey="assunto"
                          name="Assunto"
                          fill={statoColorMap.assunto}
                        />
                        <Bar
                          dataKey="non_idoneo"
                          name="Non idoneo"
                          fill={statoColorMap.non_idoneo}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </ChartWrapper>
              </CardContent>
            </StyledCard>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
